import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faPhone } from '@fortawesome/free-solid-svg-icons';
import { ShopInfo } from '../../data/fetchSettings';
import { Hours, weekRows } from '../../branch/hours';
import './branch.css';

type Lang = 'gr' | 'en';

/**
 * Address (→ map) and phone (→ call) of the branch. In a branch these come only from the
 * branch row (the server never lets the HQ address through), so there is no env fallback.
 */
export const ContactRows: React.FC<{ info: ShopInfo; lang: Lang }> = ({ info, lang }) => {
  const rows: React.ReactNode[] = [];
  if (info.addressText) {
    const body = (
      <>
        <span className="contact-row__icon" aria-hidden="true"><FontAwesomeIcon icon={faLocationDot} /></span>
        <span className="contact-row__text">
          <span className="contact-row__label">{lang === 'en' ? 'Address' : 'Διεύθυνση'}</span>
          <span className="contact-row__value">{info.addressText}</span>
        </span>
      </>
    );
    rows.push(
      <li key="address">
        {info.googleMapLink ? (
          <a className="contact-row" href={info.googleMapLink} target="_blank" rel="noopener noreferrer">{body}</a>
        ) : (
          <div className="contact-row">{body}</div>
        )}
      </li>,
    );
  }
  if (info.phoneNumber) {
    const body = (
      <>
        <span className="contact-row__icon" aria-hidden="true"><FontAwesomeIcon icon={faPhone} /></span>
        <span className="contact-row__text">
          <span className="contact-row__label">{lang === 'en' ? 'Phone' : 'Τηλέφωνο'}</span>
          <span className="contact-row__value">{info.phoneNumber}</span>
        </span>
      </>
    );
    rows.push(
      <li key="phone">
        {info.phoneLink ? <a className="contact-row" href={info.phoneLink}>{body}</a> : <div className="contact-row">{body}</div>}
      </li>,
    );
  }
  return rows.length ? <ul className="contact-rows">{rows}</ul> : null;
};

/** The week, Monday first, today highlighted. Nothing when the branch has no hours. */
export const HoursTable: React.FC<{ hours?: Hours; lang: Lang; now: Date }> = ({ hours, lang, now }) => {
  const rows = weekRows(hours, lang, now);
  if (!rows.length) return null;
  return (
    <table className="hours-table">
      <caption>{lang === 'en' ? 'Opening hours' : 'Ωράριο'}</caption>
      <tbody>
        {rows.map((r) => (
          <tr key={r.day} className={r.isToday ? 'is-today' : undefined} aria-current={r.isToday ? 'date' : undefined}>
            <th scope="row">
              {r.label}
              {r.isToday && <span className="hours-table__today">{lang === 'en' ? 'Today' : 'Σήμερα'}</span>}
            </th>
            <td>{r.text}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
